import { useEffect, useRef } from 'react';
import type React from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { AiActivityState, ArtifactAiClientError } from '@/hooks/useArtifactAi';

interface ArtifactAiActivityOverlayProps {
  mode: 'generate' | 'suggest';
  providerId?: string | null;
  model?: string | null;
  activity: AiActivityState;
  error: ArtifactAiClientError | null;
  onCancel: () => void;
}

/**
 * Live activity overlay shown in the main artifact body area while an AI
 * generate or suggest stream is in flight. Surfaces tool calls, thinking,
 * and assistant-drafting text so the user sees progress.
 */
export function ArtifactAiActivityOverlay({
  mode,
  providerId,
  model,
  activity,
  error,
  onCancel,
}: ArtifactAiActivityOverlayProps): React.ReactElement {
  const feedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = feedRef.current;
    if (el === null) return;
    el.scrollTop = el.scrollHeight;
  }, [activity.entries.length, activity.assistantTail]);

  const headerLabel = mode === 'generate' ? 'Generating' : 'Suggesting';
  const providerLabel = [providerId, model].filter((v): v is string => Boolean(v)).join(' ');

  return (
    <div className="flex flex-col gap-2">
      <div
        role="region"
        aria-label="AI activity overlay"
        className="flex flex-wrap items-center gap-2 rounded-md border border-warning/30 bg-warning/15 p-2 text-xs text-warning"
      >
        {error === null ? (
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        ) : (
          <X className="size-4 text-error" aria-hidden="true" />
        )}
        <span className="font-semibold uppercase tracking-wide">
          {error === null ? `${headerLabel} with ${providerLabel || 'AI'}…` : 'AI request failed'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            className="inline-flex gap-1.5"
          >
            <X className="size-4" />
            {error === null ? 'Cancel' : 'Close'}
          </Button>
        </div>
      </div>

      {error !== null && (
        <div
          role="alert"
          className="rounded-md border border-error/30 bg-error/15 p-2 text-xs text-error"
        >
          {error.message}
        </div>
      )}

      {error === null && (
        <div
          ref={feedRef}
          aria-live="polite"
          aria-label="AI activity feed"
          className="flex max-h-96 flex-col gap-1 overflow-auto rounded-md border border-border bg-surface/40 p-2 text-xs"
        >
          {activity.entries.length === 0 && activity.assistantTail.length === 0 ? (
            <div className="text-text-secondary italic">Starting…</div>
          ) : null}
          {activity.entries.map(entry => {
            if (entry.kind === 'thinking') {
              return (
                <div key={entry.id} className="flex items-start gap-1.5 text-text-secondary">
                  <span aria-hidden="true">💭</span>
                  <span className="italic">{entry.content}</span>
                </div>
              );
            }
            return (
              <div key={entry.id} className="flex items-center gap-1.5">
                <span aria-hidden="true">🔧</span>
                <span className="font-medium text-text-primary">{entry.toolName}</span>
                {entry.summary.length > 0 && (
                  <span className="truncate text-text-secondary">{entry.summary}</span>
                )}
                {entry.status === 'completed' ? (
                  <Check className="ml-auto size-3.5 text-success" aria-label="completed" />
                ) : (
                  <Loader2
                    className="ml-auto size-3.5 animate-spin text-text-secondary"
                    aria-label="running"
                  />
                )}
                {entry.resultBadge !== null && (
                  <Badge variant="outline" className="ml-1">
                    {entry.resultBadge}
                  </Badge>
                )}
              </div>
            );
          })}
          {activity.assistantTail.length > 0 && (
            <div className="mt-2 border-t border-border pt-2">
              <div className="mb-1 text-text-secondary">Drafting…</div>
              <div className="whitespace-pre-wrap text-text-primary">{activity.assistantTail}</div>
            </div>
          )}
          {activity.rateLimit !== null && (
            <div className="mt-2 border-t border-border pt-2 text-text-secondary">
              Within rate limit
            </div>
          )}
        </div>
      )}
    </div>
  );
}
